import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { formatDuration } from '../../lib/formatDuration';
import { useCreateQrSession, useQrSessionStatus } from '../../queries/qrSessions';
import type { QrSessionRow } from '../../db/qrSessionRepository';

declare const __BASE_PATH__: string;

export default function QRGenerator() {
  const [qrCode, setQrCode] = useState('');
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const createQrMutation = useCreateQrSession();
  const { data: sessionData } = useQrSessionStatus(isLoggedIn ? null : sessionToken);

  const generateQRCode = useCallback(async () => {
    const token = `qr-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const expiresAt = new Date(Date.now() + 60000).toISOString();

    try {
      await createQrMutation.mutateAsync({ token, expiresAt });

      const baseUrl = window.location.origin + (__BASE_PATH__ === '/' ? '' : __BASE_PATH__);
      const qrUrl = `${baseUrl}/qr-link?token=${encodeURIComponent(token)}`;

      const qrCodeUrl = await QRCode.toDataURL(qrUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });

      setQrCode(qrCodeUrl);
      setSessionToken(token);
      setTimeLeft(60);
    } catch (error) {
      console.error('QR 코드 생성 실패:', error);
    }
  }, [createQrMutation]);

  // 세션 상태 변경 감지 (useQrSessionStatus 폴링 결과)
  useEffect(() => {
    if (sessionData && sessionData.is_used && sessionData.user_id && !isLoggedIn) {
      setIsLoggedIn(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    }
  }, [sessionData, isLoggedIn, navigate]);

  // Realtime 구독 (폴링의 보완)
  useEffect(() => {
    if (!sessionToken || isLoggedIn) return;

    const channel = supabase
      .channel(`qr-session-${sessionToken}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'qr_sessions',
          filter: `session_token=eq.${sessionToken}`,
        },
        (payload) => {
          const updated = payload.new as QrSessionRow;
          queryClient.setQueryData(["qrSessions", sessionToken], updated);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionToken, isLoggedIn, queryClient]);

  useEffect(() => {
    generateQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timeLeft > 0 && !isLoggedIn) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            generateQRCode();
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, isLoggedIn]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-computer-line text-4xl text-white"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('qr.generator_title')}</h1>
            <p className="text-gray-600">{t('qr.generator_subtitle')}</p>
          </div>

          {isLoggedIn ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-5xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('qr.login_success')}</h2>
              <p className="text-gray-600">{t('qr.redirecting_dashboard')}</p>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 mb-6">
                {qrCode ? (
                  <div className="flex flex-col items-center">
                    <div className="bg-white p-4 rounded-2xl shadow-lg">
                      <img src={qrCode} alt="QR Code" className="w-64 h-64" />
                    </div>
                    <div className="mt-6 text-center">
                      <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-md">
                        <i className="ri-time-line text-xl text-teal-600"></i>
                        <span className="text-2xl font-bold text-gray-900">{formatDuration(timeLeft, t)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-3">{t('qr.time_remaining')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex gap-3">
                  <i className="ri-information-line text-xl text-blue-600 flex-shrink-0 mt-0.5"></i>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">{t('qr.how_to_use')}</p>
                    <ol className="list-decimal list-inside space-y-1 text-blue-700">
                      <li>{t('qr.step_1')}</li>
                      <li>{t('qr.step_2')}</li>
                      <li>{t('qr.step_3')}</li>
                    </ol>
                  </div>
                </div>
              </div>

              <Link
                to="/login"
                className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                {t('qr.back_to_login')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
